import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, X, Landmark, Home } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { badgeStyle } from "@/lib/badgeColor";
import { toast } from "sonner";

const LISTS = [
  { key: "asset_category", label: "Asset Category", parent: null, factor: false },
  { key: "asset_type", label: "Asset Type", parent: "asset_category", factor: false },
  { key: "price_unit", label: "Price Unit", parent: null, factor: true, factorLabel: "₹ per 1 unit" },
  { key: "area_unit", label: "Area Unit", parent: null, factor: true, factorLabel: "Sq.Ft per 1 unit" },
  { key: "city", label: "City", parent: null, factor: false },
  { key: "village_town", label: "Village / Town", parent: "city", factor: false },
  { key: "sale_type", label: "Sale Type", parent: null, factor: false, sections: [
    { value: "auction", title: "Auction Assets", icon: Landmark, addLabel: "Add Auction Sale Type" },
    { value: "resale", title: "Land & Other Assets", icon: Home, addLabel: "Add Resale Sale Type" },
  ] },
  { key: "bank_arc", label: "Bank / ARC", parent: null, factor: false },
  { key: "possession_type", label: "Possession Type", parent: null, factor: false },
  { key: "case_forum", label: "Case Forum", parent: null, factor: false },
  { key: "document_type", label: "Document Type", parent: null, factor: false },
  { key: "asset_status", label: "Asset Status", parent: null, factor: false, badge: true },
  // Module 2 — Leads
  { key: "lead_source", label: "Lead Source", parent: null, factor: false },
  { key: "lead_quality", label: "Lead Quality", parent: null, factor: false, badge: true },
  { key: "lead_status", label: "Lead Status", parent: null, factor: false, badge: true },
  { key: "lead_warmth", label: "Lead Warmth", parent: null, factor: false, badge: true },
  { key: "buyer_type", label: "Buyer Type", parent: null, factor: false },
  { key: "nature_of_business", label: "Nature of Business", parent: null, factor: false, badge: true },
  { key: "activity_type", label: "Activity Type", parent: null, factor: false },
  { key: "activity_outcome", label: "Activity Outcome", parent: null, factor: false },
  { key: "closure_reason", label: "Closure Reason", parent: null, factor: false },
  { key: "tags", label: "Tags", parent: null, factor: false },
  // Module 3 — Matches & Deals
  { key: "match_status", label: "Match Status", parent: null, factor: false, badge: true },
  { key: "match_outcome_reason", label: "Match Outcome Reason", parent: null, factor: false },
  { key: "deal_status", label: "Deal Status", parent: null, factor: false, badge: true },
  // Module 7 / 8 — Brokers & Offers
  { key: "broker_type", label: "Broker Type", parent: null, factor: false },
  { key: "broker_relationship_status", label: "Broker Relationship Status", parent: null, factor: false, badge: true },
  { key: "broker_share_status", label: "Broker Share Status", parent: null, factor: false, badge: true },
  { key: "commission_split_type", label: "Commission Split Type", parent: null, factor: false },
  { key: "offer_status", label: "Offer Status", parent: null, factor: false, badge: true },
  { key: "broker_meeting_outcome", label: "Broker Meeting Outcome", parent: null, factor: false, badge: true },
];

const DEFAULT_NEW_COLOR = "#2563EB";

function useListAll(listType) {
  return useQuery({
    queryKey: ["master-admin", listType],
    queryFn: async () => {
      const { data } = await api.get(`/master-data/${listType}`, {
        params: { include_inactive: true, include_pending: true },
      });
      return data;
    },
    staleTime: 0,
  });
}

function ListEditor({ list }) {
  const qc = useQueryClient();
  const { data: values = [], isLoading } = useListAll(list.key);
  const { data: parents = [] } = useQuery({
    queryKey: ["master-admin", list.parent],
    queryFn: async () =>
      list.parent ? (await api.get(`/master-data/${list.parent}`, { params: { include_inactive: true } })).data : [],
    enabled: !!list.parent,
  });

  const parentMap = useMemo(() => Object.fromEntries(parents.map((p) => [p.id, p.label])), [parents]);

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", parent_id: "", conversion_factor: "", badge_color: DEFAULT_NEW_COLOR, is_active: true, asset_section: null });

  const startAdd = (assetSection = null) => {
    setEditing(null);
    setForm({ label: "", parent_id: "", conversion_factor: "", badge_color: DEFAULT_NEW_COLOR, is_active: true, asset_section: assetSection });
    setAddOpen(true);
  };
  const startEdit = (v) => {
    setEditing(v);
    setForm({
      label: v.label,
      parent_id: v.parent_id || "",
      conversion_factor: v.conversion_factor ?? "",
      badge_color: v.badge_color || DEFAULT_NEW_COLOR,
      is_active: v.is_active,
      asset_section: v.asset_section ?? null,
    });
    setAddOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        list_type: list.key,
        label: form.label.trim(),
        parent_id: list.parent ? form.parent_id || null : null,
        conversion_factor: list.factor && form.conversion_factor !== "" ? Number(form.conversion_factor) : null,
        is_active: form.is_active,
      };
      if (list.badge) body.badge_color = form.badge_color;
      if (list.sections) body.asset_section = form.asset_section || null;
      if (editing) {
        return api.patch(`/master-data/${list.key}/${editing.id}`, body);
      }
      return api.post(`/master-data/${list.key}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["master-admin", list.key] });
      qc.invalidateQueries({ queryKey: ["master", list.key] });
      toast.success(editing ? "Updated" : "Added");
      setAddOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const approve = useMutation({
    mutationFn: async (id) => api.patch(`/master-data/${list.key}/${id}`, { review_status: "approved" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["master-admin", list.key] });
      qc.invalidateQueries({ queryKey: ["master", list.key] });
      toast.success("Approved");
    },
  });

  const toggleActive = useMutation({
    mutationFn: async (v) => api.patch(`/master-data/${list.key}/${v.id}`, { is_active: !v.is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["master-admin", list.key] });
      qc.invalidateQueries({ queryKey: ["master", list.key] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  // Active + unprotected: try permanent delete; backend deactivates instead if referenced.
  const removeValue = useMutation({
    mutationFn: async (v) => (await api.delete(`/master-data/${list.key}/${v.id}`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["master-admin", list.key] });
      qc.invalidateQueries({ queryKey: ["master", list.key] });
      if (data?.action === "deleted") {
        toast.success("Value permanently deleted");
      } else {
        toast.info(`In use by ${data?.references} record(s) — deactivated instead of deleted`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const handleRemoveOrToggle = (v) => {
    if (!v.is_active) {
      toggleActive.mutate(v); // reactivate
      return;
    }
    if (!window.confirm("Remove this value? If it isn't used anywhere it will be permanently deleted; otherwise it will be deactivated.")) return;
    removeValue.mutate(v);
  };

  const colCount = 3 + (list.badge ? 1 : 0) + (list.parent ? 1 : 0) + (list.factor ? 1 : 0);

  const renderRow = (v) => (
    <tr key={v.id} className="border-t border-slate-100" data-testid={`master-row-${v.id}`}>
      <td className="text-sm font-medium text-slate-900">{v.label}</td>
      {list.badge && (
        <td>
          <span
            style={badgeStyle(v.badge_color)}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border"
            data-testid={`master-badge-preview-${v.id}`}
          >
            {v.label}
          </span>
        </td>
      )}
      {list.parent && <td className="text-sm text-slate-700">{parentMap[v.parent_id] || "—"}</td>}
      {list.factor && <td className="text-right font-mono-num text-sm">{v.conversion_factor ?? "—"}</td>}
      <td>
        <div className="flex items-center gap-1.5">
          {v.is_active ? (
            <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700">Active</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">Inactive</Badge>
          )}
          {v.review_status === "pending" && (
            <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700">Pending</Badge>
          )}
          {v.is_protected && (
            <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700" data-testid={`protected-badge-${v.id}`}>Protected</Badge>
          )}
        </div>
      </td>
      <td>
        <div className="flex items-center justify-end gap-1">
          {v.review_status === "pending" && (
            <Button size="sm" variant="outline" onClick={() => approve.mutate(v.id)} data-testid={`master-approve-${v.id}`}>
              <Check className="h-3.5 w-3.5 mr-1" /> Approve
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => startEdit(v)} data-testid={`master-edit-${v.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={v.is_protected && v.is_active}
            title={v.is_protected && v.is_active ? "Protected — cannot remove" : (v.is_active ? "Delete (deactivates if in use)" : "Reactivate")}
            onClick={() => handleRemoveOrToggle(v)}
            data-testid={`master-toggle-${v.id}`}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );

  const renderTable = (rows, emptyText = "No values yet.") => (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <table className="data-table w-full">
        <thead>
          <tr>
            <th className="label-eyebrow !text-slate-600">Label</th>
            {list.badge && <th className="label-eyebrow !text-slate-600">Badge</th>}
            {list.parent && <th className="label-eyebrow !text-slate-600">Parent</th>}
            {list.factor && <th className="label-eyebrow !text-slate-600 text-right">{list.factorLabel}</th>}
            <th className="label-eyebrow !text-slate-600">Status</th>
            <th className="label-eyebrow !text-slate-600 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={colCount} className="text-center py-6 text-sm text-slate-500">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={colCount} className="text-center py-6 text-sm text-slate-500">{emptyText}</td></tr>
          ) : rows.map(renderRow)}
        </tbody>
      </table>
    </div>
  );

  const knownSections = list.sections ? list.sections.map((s) => s.value) : [];
  const ungrouped = list.sections ? values.filter((v) => !knownSections.includes(v.asset_section)) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display text-lg font-semibold text-slate-900">{list.label}</div>
          <div className="text-xs text-slate-500">{values.length} entries</div>
        </div>
        {!list.sections && (
          <Button onClick={() => startAdd()} className="bg-slate-900 hover:bg-slate-800" data-testid={`add-master-${list.key}`}>
            <Plus className="h-4 w-4 mr-1.5" /> Add value
          </Button>
        )}
      </div>

      {list.sections ? (
        <div className="space-y-7">
          {list.sections.map((sec) => {
            const rows = values.filter((v) => v.asset_section === sec.value);
            const Icon = sec.icon;
            return (
              <div key={sec.value} data-testid={`master-section-${list.key}-${sec.value}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="label-eyebrow !text-slate-700">{sec.title}</span>
                    <span className="text-xs text-slate-400">({rows.length})</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startAdd(sec.value)}
                    data-testid={`add-master-${list.key}-${sec.value}`}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> {sec.addLabel}
                  </Button>
                </div>
                {renderTable(rows)}
              </div>
            );
          })}
          {ungrouped.length > 0 && (
            <div data-testid={`master-section-${list.key}-other`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="label-eyebrow !text-slate-700">Ungrouped</span>
                <span className="text-xs text-slate-400">({ungrouped.length})</span>
              </div>
              {renderTable(ungrouped)}
            </div>
          )}
        </div>
      ) : (
        renderTable(values)
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit value" : "Add value"} — {list.label}{list.sections && form.asset_section ? ` · ${list.sections.find((s) => s.value === form.asset_section)?.title || form.asset_section}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" data-testid="master-form-label" />
            </div>
            {list.parent && (
              <div>
                <Label>Parent ({LISTS.find((l) => l.key === list.parent)?.label})</Label>
                <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="master-form-parent"><SelectValue placeholder="Select parent…" /></SelectTrigger>
                  <SelectContent>
                    {parents.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {list.factor && (
              <div>
                <Label>{list.factorLabel}</Label>
                <Input type="number" step="0.0001" value={form.conversion_factor} onChange={(e) => setForm({ ...form, conversion_factor: e.target.value })} className="mt-1 font-mono-num" data-testid="master-form-factor" />
              </div>
            )}
            {list.badge && (
              <div>
                <Label>Badge Color</Label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="color"
                    value={form.badge_color}
                    onChange={(e) => setForm({ ...form, badge_color: e.target.value })}
                    className="h-9 w-12 rounded border border-slate-200 cursor-pointer bg-white p-0.5"
                    data-testid="master-form-color"
                  />
                  <Input
                    value={form.badge_color}
                    onChange={(e) => setForm({ ...form, badge_color: e.target.value })}
                    className="w-28 font-mono-num uppercase"
                    data-testid="master-form-color-hex"
                  />
                  <span
                    style={badgeStyle(form.badge_color)}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border"
                    data-testid="master-form-color-preview"
                  >
                    {form.label.trim() || "Preview"}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">One color drives both the badge background (light tint) and text (darker shade).</p>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="master-form-active" />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.label.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="master-form-save">
              {editing ? "Save changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MasterData() {
  return (
    <div className="p-8">
      <div className="label-eyebrow">Admin</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Master Data</h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        Manage taxonomy lists used across the platform. Approve Pending Review entries created from forms.
      </p>

      <Tabs defaultValue="asset_category" className="mt-6">
        <div className="overflow-x-auto scrollbar-thin">
          <TabsList className="bg-slate-100 inline-flex">
            {LISTS.map((l) => (
              <TabsTrigger key={l.key} value={l.key} data-testid={`tab-master-${l.key}`}>
                {l.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {LISTS.map((l) => (
          <TabsContent key={l.key} value={l.key} className="mt-5">
            <ListEditor list={l} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
